import { useState } from 'react'
import {
  Box, Button, Card, Field, Flex, HStack, Heading, Input, NativeSelect,
  Skeleton, Stack, Switch, Text,
} from '@chakra-ui/react'
import { useAuth } from '../contexts/AuthContext'
import { useEventTypes } from '../contexts/EventTypesContext'
import { updateEventType } from '../services/firebaseService'
import { DEFAULT_EVENT_TYPES, TYPE_SECTION_IDS } from '../constants/eventTypes'
import { VIDEO_AI_FEATURES, DEFAULT_VIDEO_AI_CONFIG, EXPLICIT_THRESHOLDS } from '../constants/videoAI'
import { Challenges } from '../components/Challenges'
import { PageHeader } from '../components/ui/PageHeader'
import { toaster } from '../components/ui/toaster'
import { useTranslation } from '../i18n'

const TAG_COLORS = ['gray', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'cyan', 'purple', 'pink']
const ADMIN_ROLES = ['eventadmin', 'weddingadmin']

function TypeEditor({ type }) {
  const { t } = useTranslation()
  const [labelEn, setLabelEn] = useState(type.label?.en || '')
  const [labelEs, setLabelEs] = useState(type.label?.es || '')
  const [tagColor, setTagColor] = useState(type.tagColor || 'gray')
  const [adminRole, setAdminRole] = useState(type.adminRole || 'eventadmin')
  const [moderation, setModeration] = useState(type.capabilities?.moderation !== false)
  const [prizes, setPrizes] = useState(type.capabilities?.prizes !== false)
  const [hidden, setHidden] = useState(type.hiddenSections || [])
  // Structured defaults state. `challenges` is edited via the shared Challenges
  // component (same UI as the event editor); scalars via their own controls.
  // Stored type docs predate the streaming defaults, so a type that has them in
  // its bundled config (wedding) falls back to those — the server does too.
  // Others leave the keys absent until set: a type change copies every
  // `defaults` key, and a stored `streamEnabled: false` would tear an event's
  // stream down on the way in.
  const [defaults, setDefaults] = useState(() => {
    const bundled = DEFAULT_EVENT_TYPES[type.id]?.defaults || {}
    const stream = {}
    for (const key of ['streamEnabled', 'galleryPublic']) {
      if (bundled[key] !== undefined) stream[key] = bundled[key]
    }
    return { challenges: [], ...stream, ...(type.defaults || {}) }
  })
  const [videoAI, setVideoAI] = useState(() => ({
    ...DEFAULT_VIDEO_AI_CONFIG,
    ...(type.videoAI || {}),
    features: { ...DEFAULT_VIDEO_AI_CONFIG.features, ...(type.videoAI?.features || {}) },
  }))
  const [saving, setSaving] = useState(false)

  const setDefault = (key, value) => setDefaults((prev) => ({ ...prev, [key]: value }))
  const setAIFeature = (key, checked) =>
    setVideoAI((prev) => ({ ...prev, features: { ...prev.features, [key]: checked } }))

  const toggleHidden = (id) =>
    setHidden((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])

  const handleSave = async () => {
    try {
      setSaving(true)
      await updateEventType(type.id, {
        label: { en: labelEn, es: labelEs },
        tagColor,
        adminRole,
        capabilities: { moderation, prizes },
        hiddenSections: hidden,
        defaults,
        videoAI,
      })
      toaster.create({ title: t('eventTypes.saved'), status: 'success' })
    } catch (err) {
      toaster.create({ title: t('eventTypes.saveFailed'), description: err.message, status: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const roleLabel = (role) =>
    role === 'weddingadmin' ? t('eventUsers.weddingOrganizer') : t('eventUsers.eventAdmin')

  return (
    <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
      <Card.Body>
        <Stack gap={3}>
          <HStack justify="space-between">
            <Heading size="sm">{type.id}</Heading>
            <HStack gap={2}>
              <Text fontSize="xs" color="fg.muted">{t('eventTypes.slug')}: <b>/{type.urlSlug}</b></Text>
              <Text fontSize="xs" color="fg.muted">{t('eventTypes.entryFlow')}: <b>{type.entryFlow}</b></Text>
            </HStack>
          </HStack>

          <Flex gap={3} wrap="wrap">
            <Field.Root flex="1" minW="140px">
              <Field.Label>{t('eventTypes.labelEn')}</Field.Label>
              <Input value={labelEn} onChange={(e) => setLabelEn(e.target.value)} />
            </Field.Root>
            <Field.Root flex="1" minW="140px">
              <Field.Label>{t('eventTypes.labelEs')}</Field.Label>
              <Input value={labelEs} onChange={(e) => setLabelEs(e.target.value)} />
            </Field.Root>
          </Flex>

          <Flex gap={3} wrap="wrap">
            <Field.Root minW="160px">
              <Field.Label>{t('eventTypes.tagColor')}</Field.Label>
              <NativeSelect.Root size="sm" width="160px">
                <NativeSelect.Field value={tagColor} onChange={(e) => setTagColor(e.target.value)} bg="var(--mc-input-bg)">
                  {TAG_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
            <Field.Root minW="220px">
              <Field.Label>{t('eventTypes.adminRole')}</Field.Label>
              <NativeSelect.Root size="sm" width="220px">
                <NativeSelect.Field value={adminRole} onChange={(e) => setAdminRole(e.target.value)} bg="var(--mc-input-bg)">
                  {ADMIN_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          </Flex>

          <HStack gap={6}>
            <Switch.Root checked={moderation} onCheckedChange={(d) => setModeration(d.checked)} colorPalette="green">
              <Switch.HiddenInput /><Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('eventTypes.capModeration')}</Switch.Label>
            </Switch.Root>
            <Switch.Root checked={prizes} onCheckedChange={(d) => setPrizes(d.checked)}>
              <Switch.HiddenInput /><Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('eventTypes.capPrizes')}</Switch.Label>
            </Switch.Root>
          </HStack>

          <Box>
            <Text fontWeight="600" mb={1}>{t('eventTypes.hiddenSections')}</Text>
            <Flex gap={2} wrap="wrap">
              {TYPE_SECTION_IDS.map((id) => (
                <Button
                  key={id}
                  size="xs"
                  variant={hidden.includes(id) ? 'solid' : 'outline'}
                  colorPalette={hidden.includes(id) ? 'red' : 'gray'}
                  onClick={() => toggleHidden(id)}
                >
                  {id}
                </Button>
              ))}
            </Flex>
          </Box>

          {/* Default values applied to a new event of this type (and to a type
              change, except user content which is preserved). */}
          <Text fontWeight="600">{t('eventTypes.defaults')}</Text>
          <Flex gap={4} wrap="wrap">
            <Field.Root minW="140px">
              <Field.Label>{t('events.maxSegmentDuration')}</Field.Label>
              <Input
                type="number"
                min={1}
                value={defaults.maxSegmentDuration ?? ''}
                onChange={(e) => setDefault('maxSegmentDuration', e.target.value === '' ? '' : parseFloat(e.target.value))}
                w="100px"
              />
            </Field.Root>
            <Field.Root minW="140px">
              <Field.Label>{t('events.maxRecordingDuration')}</Field.Label>
              <Input
                type="number"
                min={1}
                value={defaults.maxRecordingDuration ?? ''}
                onChange={(e) => setDefault('maxRecordingDuration', e.target.value === '' ? '' : parseFloat(e.target.value))}
                w="100px"
              />
            </Field.Root>
          </Flex>
          <HStack gap={6}>
            <Switch.Root checked={defaults.defaultModerated ?? false} onCheckedChange={(d) => setDefault('defaultModerated', d.checked)} colorPalette="green">
              <Switch.HiddenInput /><Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('events.autoApprove')}</Switch.Label>
            </Switch.Root>
            <Switch.Root checked={defaults.receptionEnabled ?? true} onCheckedChange={(d) => setDefault('receptionEnabled', d.checked)}>
              <Switch.HiddenInput /><Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('events.acceptSubmissions')}</Switch.Label>
            </Switch.Root>
          </HStack>
          <HStack gap={6} wrap="wrap">
            <Switch.Root
              checked={!!defaults.streamEnabled}
              onCheckedChange={(d) => setDefaults((prev) => ({
                ...prev,
                streamEnabled: d.checked,
                // The gallery switch only means something while streaming is on.
                galleryPublic: d.checked ? prev.galleryPublic : false,
              }))}
            >
              <Switch.HiddenInput /><Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('events.enableStreaming')}</Switch.Label>
            </Switch.Root>
            <Switch.Root
              checked={!!defaults.galleryPublic}
              disabled={!defaults.streamEnabled}
              onCheckedChange={(d) => setDefault('galleryPublic', d.checked)}
            >
              <Switch.HiddenInput /><Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('events.galleryPublic')}</Switch.Label>
            </Switch.Root>
          </HStack>

          {/* Default challenges — same editor as the event config. */}
          <Challenges form={defaults} setForm={setDefaults} editing={true} />

          <Stack gap={2}>
            <Text fontWeight="600">{t('videoAI.title')}</Text>
            <Switch.Root
              checked={videoAI.enabled}
              onCheckedChange={(d) => setVideoAI((p) => ({ ...p, enabled: d.checked }))}
            >
              <Switch.HiddenInput /><Switch.Control /><Switch.Label>{t('videoAI.enable')}</Switch.Label>
            </Switch.Root>
            {videoAI.enabled && (
              <>
                {VIDEO_AI_FEATURES.map((f) => (
                  <Switch.Root
                    key={f.key}
                    checked={videoAI.features[f.key] === true}
                    onCheckedChange={(d) => setAIFeature(f.key, d.checked)}
                    size="sm"
                  >
                    <Switch.HiddenInput /><Switch.Control />
                    <Switch.Label>{t(f.labelKey)}</Switch.Label>
                  </Switch.Root>
                ))}
                {videoAI.features.explicitContent && (
                  <>
                    <Switch.Root
                      checked={videoAI.aiModeration}
                      onCheckedChange={(d) => setVideoAI((p) => ({ ...p, aiModeration: d.checked }))}
                      colorPalette="red"
                    >
                      <Switch.HiddenInput /><Switch.Control />
                      <Switch.Label>{t('videoAI.aiModeration')}</Switch.Label>
                    </Switch.Root>
                    <NativeSelect.Root size="sm" width="200px">
                      <NativeSelect.Field
                        value={videoAI.explicitThreshold}
                        onChange={(e) => setVideoAI((p) => ({ ...p, explicitThreshold: e.target.value }))}
                      >
                        {EXPLICIT_THRESHOLDS.map((level) => (
                          <option key={level} value={level}>{t(`videoAI.threshold${level}`)}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </>
                )}
              </>
            )}
          </Stack>

          <Flex justify="flex-end">
            <Button size="sm" colorPalette="brand" loading={saving} loadingText={t('common.saving')} onClick={handleSave}>{t('common.save')}</Button>
          </Flex>
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}

export default function EventTypesPage() {
  const { isSysAdmin } = useAuth()
  const { t } = useTranslation()
  const { typesList, loading } = useEventTypes()

  if (!isSysAdmin) return (<Text color="var(--mc-steel)">{t('common.noPermission')}</Text>)

  return (
    <Stack gap={6}>
      <PageHeader title={t('eventTypes.title')} subtitle={t('eventTypes.subtitle')} />
      {loading ? (
        <Stack gap={4}>{[1, 2, 3].map((i) => <Skeleton key={i} height="200px" borderRadius="md" />)}</Stack>
      ) : (
        <Stack gap={4}>
          {typesList.map((type) => <TypeEditor key={type.id} type={type} />)}
        </Stack>
      )}
    </Stack>
  )
}
